<script setup lang="ts">
import type { NuxtError } from '#app'

initializeIrWebsite({ index: false })

interface Props {
  error: NuxtError
}

const props = defineProps<Props>()

const { store } = useReportNotFoundUrlApi()

// Legacy `statusCode`/`statusMessage` shims for older Nuxt error payloads; remove on next Nuxt major.
const status = computed<number | undefined>(() => props.error?.status ?? props.error?.statusCode)
const statusText = computed<string | undefined>(() => props.error?.statusText ?? props.error?.statusMessage ?? props.error?.message)

const displayMessage = computed<string>(() => {
  if (status.value === 404) return 'Page not found.'
  if (status.value === 403) return statusText.value || 'You do not have permission to perform this action.'
  return 'Something went wrong. Please try again.'
})

onMounted(() => {
  if (status.value === 404) {
    store(window.location.href).catch(useHandleException)
  }
})

useHead({
  title: 'Page Not Found',
})
</script>

<template>
  <section>
    <NuxtLayout>
      <div
        v-if="error"
        class="error-page"
      >
        <h1 class="error-page__title">
          {{ status }}
        </h1>
        <p class="error-page__text">
          {{ displayMessage }}
        </p>
        <NuxtLink
          to="/"
          class="button error-page__button"
        >
          Return To Home
        </NuxtLink>
      </div>
    </NuxtLayout>
  </section>
</template>

<style scoped lang="scss">
.error-page {
  @apply w-full min-h-[500px] flex flex-col items-center justify-center gap-y-[20px];

  &__title {
    @apply text-[30px];
    @apply md:text-[40px];
    @apply xl:text-[50px];
  }

  &__text {
    @apply text-[14px];
    @apply md:text-[18px];
  }

  &__button {
    @apply bg-dark-background text-light-text border-2 border-transparent;
    @apply hover:bg-base-background hover:border-dark-background hover:text-dark-text;
  }
}
</style>
